import type { WarningNotice } from '@/pages/accounting/statements/statementMeta';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import type { LocaleCode } from '@/api/models';
import type { ErrorResponse } from '@/api/handlers/responses';
import { useVatReportQuery, VatQueryKeys } from '@/api/queries';
import { formatGel } from '@/pages/accounting/books/chartOfAccounts/utils';
import {
    formatGeneratedAt,
    resolveLocale,
    todayIso,
    unwrapStatement,
} from '@/pages/accounting/statements/statementMeta';
import { PATH } from '@/router/PATH';
import dayjs from 'dayjs';
import { useBooksRange } from '../booksOutletContext';
import { vatFilingDueDate, vatFilingWindow } from '../booksWindow';
import {
    netDirection,
    toSideDrillTargets,
    toVatRows,
    type NetDirection,
    type VatDrillTarget,
    type VatRow,
} from './vatMeta';

export type UseVatReportPageResult = {
    /**
     * The window the figures on screen actually cover, echoed by the server.
     * Falls back to the requested range before the first report resolves.
     *
     * Separate from the shell's range on purpose: with `keepPreviousData` the
     * previous report stays on screen while a new range loads, so what the
     * figures cover and what the picker reads can differ for a moment. These are
     * the figures' own dates — the settlement and the drill must use them.
     */
    reportedFrom: string;
    reportedTo: string;
    /** The month a return filed today covers — always the previous one. */
    filingFrom: string;
    filingTo: string;
    /** When that return is due: the 15th of the month after the period. */
    filingDueDate: string;
    /** Non-null when the window on screen is not the one being filed. */
    filingMismatch: { from: string; to: string } | null;
    onShowFilingPeriod: () => void;
    locale: LocaleCode;
    isLoading: boolean;
    error: ErrorResponse | null;
    onRetry: () => void;
    /** Null until the first load resolves. */
    summary: {
        outputText: string;
        inputText: string;
        /** Magnitude of the net, formatted; direction carries the sign meaning. */
        netText: string;
        netTetri: number;
        direction: NetDirection;
        /** `null` when the ledger has no settlement account — see `settlementBlock`. */
        settlementAccountCode: string | null;
    } | null;
    outputRows: VatRow[];
    inputRows: VatRow[];
    /** The accounts behind each side's total — the multi-account drill case. */
    outputDrillTargets: VatDrillTarget[];
    inputDrillTargets: VatDrillTarget[];
    warningNotices: WarningNotice[];
    onDismissWarnings: () => void;
    onViewLedger: (accountId: string) => void;
    /** Refetch the report after a settlement posts. */
    onSettled: () => void;
    generatedText: string;
};

/**
 * Drives the VAT report page: fetches the report for a period and a locale,
 * formats the summary figures and breakdown rows (pure), and owns the warning
 * state.
 *
 * Unlike its `/accounting` counterpart it owns **no range state and no
 * presets** — the Books shell holds one date range for every tab, and a second
 * period control on the page would be a second answer to the same question. The
 * report simply reads the shell's range.
 *
 * It does not classify anything, and it does not sum anything. Which accounts
 * are input and which are output is the server's `vatRole` tag, resolved at the
 * mapping boundary; both side totals and the net arrive computed. The page
 * renders what it is given.
 */
export const useVatReportPage = (): UseVatReportPageResult => {
    const { i18n } = useTranslation();
    const locale = resolveLocale(i18n.language);
    const navigate = useNavigate();
    const queryClient = useQueryClient();

    const today = React.useRef(todayIso()).current;
    // The shell's range, with the page's old default as a floor — an empty one
    // (this page rendered outside the shell) would otherwise request a report
    // for a window with no dates in it.
    const { fromDate, toDate, onRangeChange } = useBooksRange();

    // BOS-166: a VAT return is filed *after* the period it reports on, so the
    // month being filed today is always the previous one. The shell's window
    // may be anything — month-to-date on the 2nd meant this tab showed zeros
    // and a disabled settle button while the accountant was there to settle
    // August.
    const filing = React.useMemo(() => vatFilingWindow(dayjs(today)), [today]);
    const range = {
        from: fromDate || filing.from,
        to: toDate || filing.to,
    };

    /**
     * Whether the shell is showing something other than the month being filed,
     * and the one click that fixes it. Null when they already agree — an offer
     * to go where you are is noise.
     */
    const filingMismatch =
        range.from === filing.from && range.to === filing.to
            ? null
            : { from: filing.from, to: filing.to };

    const onShowFilingPeriod = React.useCallback(() => {
        onRangeChange?.(filing.from, filing.to);
    }, [filing.from, filing.to, onRangeChange]);

    const filingDueDate = React.useMemo(
        () => vatFilingDueDate(dayjs(today)),
        [today],
    );
    const [warningsDismissed, setWarningsDismissed] = React.useState(false);
    const generatedText = React.useRef(formatGeneratedAt(new Date())).current;

    const query = useVatReportQuery(range.from, range.to, locale);
    const { data, error } = unwrapStatement(query.data, query.error);

    React.useEffect(() => {
        setWarningsDismissed(false);
    }, [range.from, range.to]);

    const summary = React.useMemo(() => {
        if (!data) {
            return null;
        }
        return {
            outputText: formatGel(data.output.totalTetri),
            inputText: formatGel(data.input.totalTetri),
            // The magnitude only — `direction` is what says which way it points,
            // in words. A bare minus sign reads as an error to most people.
            netText: formatGel(Math.abs(data.netTetri)),
            netTetri: data.netTetri,
            direction: netDirection(data.netTetri),
            settlementAccountCode: data.settlementAccount.accountCode,
        };
    }, [data]);

    const outputRows = React.useMemo(
        () => (data ? toVatRows(data.output.lines) : []),
        [data],
    );
    const inputRows = React.useMemo(
        () => (data ? toVatRows(data.input.lines) : []),
        [data],
    );
    const outputDrillTargets = React.useMemo(
        () => (data ? toSideDrillTargets(data.output.lines) : []),
        [data],
    );
    const inputDrillTargets = React.useMemo(
        () => (data ? toSideDrillTargets(data.input.lines) : []),
        [data],
    );

    // The VAT report's own warnings are already localized by the server and
    // carry no rule this app writes itself, so they pass through as `raw`
    // notices — the shape the advisory banner now takes (BOS-163). Advisory
    // only: never an error, never blocking the report.
    const warningNotices = React.useMemo<WarningNotice[]>(() => {
        if (!data || warningsDismissed) {
            return [];
        }
        return data.warnings.map((warning) => ({
            kind: 'raw' as const,
            text: warning.message,
        }));
    }, [data, warningsDismissed]);

    const onRetry = React.useCallback(() => {
        query.refetch();
    }, [query]);

    // The drill carries the report's own window. A VAT report is a statement
    // about a period, so a drill that landed on the journal's default month
    // would reach the right account and show nothing — which is the dead link
    // this integration exists to fix, wearing a different hat.
    const drillFrom = data?.window.from ?? range.from;
    const drillTo = data?.window.to ?? range.to;
    const onViewLedger = React.useCallback(
        (accountId: string) => {
            navigate(
                PATH.ACCOUNTING.BOOKS.BIND_JOURNAL_FOR_ACCOUNT(accountId, {
                    from: drillFrom,
                    to: drillTo,
                }),
            );
        },
        [navigate, drillFrom, drillTo],
    );

    const onSettled = React.useCallback(() => {
        queryClient.invalidateQueries({ queryKey: VatQueryKeys.all });
    }, [queryClient]);

    return {
        // What the figures on screen cover is the server's echo of the window,
        // not what the shell's picker currently reads. They agree today; if they
        // ever stop, the period shown belongs to the report, not to the request.
        reportedFrom: data?.window.from ?? range.from,
        reportedTo: data?.window.to ?? range.to,
        /**
         * The month a return filed today covers, and when it is due — so the
         * tab can say which period it is showing rather than leaving a reader
         * to work it out from two dates (BOS-166).
         */
        filingFrom: filing.from,
        filingTo: filing.to,
        filingDueDate,
        /** Non-null when the window on screen is *not* the one being filed. */
        filingMismatch,
        onShowFilingPeriod,
        locale,
        isLoading: query.isLoading,
        error,
        onRetry,
        summary,
        outputRows,
        inputRows,
        outputDrillTargets,
        inputDrillTargets,
        warningNotices,
        onDismissWarnings: () => setWarningsDismissed(true),
        onViewLedger,
        onSettled,
        generatedText,
    };
};
